import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Layout from '@/components/layout/Layout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import SessionTimeoutWarning from '@/components/auth/SessionTimeoutWarning';
import AuthProvider from '@/providers/AuthProvider';
import { useAuth } from '@/hooks/useAuth';
import { ClientProvider } from '@/contexts/ClientContext';
import { useClient } from '@/contexts/ClientContext';
import { ViewModeProvider } from '@/contexts/ViewModeContext';
import { ToastProvider } from '@/components/ui/Toast';
import { WaveLoader } from '@/components/ui/WaveLoader';
import StagingTape from '@/components/staging/StagingTape';

// Critical pages - loaded immediately
import Login from '@/pages/Login';
import OAuthCallback from '@/pages/OAuthCallback';
import Welcome from '@/pages/Welcome';

// Lazy-loaded pages - code split for faster initial load
const Deposits = lazy(() => import('@/pages/Deposits'));
const EntryPeriodsPage = lazy(() => import('@/pages/EntryPeriods').then(m => ({ default: m.EntryPeriodsPage })));
const Privacy = lazy(() => import('@/pages/Privacy'));

// Loading animation for lazy components
const PageLoader = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <WaveLoader size="lg" />
  </div>
);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Never retry 429 (rate limited) or 401/403 (auth errors) — retrying makes it worse
        const status = error?.response?.status;
        if (status === 429 || status === 401 || status === 403) return false;
        return failureCount < 1; // 1 retry for other errors
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Wrapper component for EntryPeriods that gets selectedClient from context
const EntryPeriodsWrapper: React.FC = () => {
  const { selectedClient } = useClient();
  return <EntryPeriodsPage selectedClient={selectedClient} />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100">
        <WaveLoader size="xl" />
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public homepage - NOT behind login (required for Google OAuth verification) */}
        <Route
          path="/"
          element={
            isAuthenticated ? <Navigate to="/deposits" replace /> : <Login />
          }
        />
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/deposits" replace /> : <Login />
          }
        />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><Privacy /></Suspense>} />

        {/* Protected routes - app functionality behind auth */}
        <Route
          path="/deposits"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Suspense fallback={<PageLoader />}><Deposits /></Suspense>} />
        </Route>
        <Route
          path="/entry-periods"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Suspense fallback={<PageLoader />}><EntryPeriodsWrapper /></Suspense>} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace /> } />
      </Routes>
      <StagingTape />
    </Router>
  );
};

// Wrapper to provide ViewModeProvider with user email from auth context
const ViewModeWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  return (
    <ViewModeProvider userEmail={user?.email}>
      {children}
    </ViewModeProvider>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ViewModeWrapper>
          <ClientProvider>
            <ToastProvider>
              <AppContent />
              {/* HIPAA Session Timeout Warning - CFR § 164.312(a)(2)(iii) */}
              <SessionTimeoutWarning />
            </ToastProvider>
          </ClientProvider>
        </ViewModeWrapper>
        <ReactQueryDevtools initialIsOpen={false} />
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
